import { useEffect, useState } from "react";
import api, { eur, num } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileSpreadsheet, Printer, Loader2, BarChart3, TrendingUp, ArrowUp, ArrowDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Reports() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [partners, setPartners] = useState([]);
  const [redd, setRedd] = useState(null);
  const [reddYear, setReddYear] = useState("all");

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
    api.get("/partners").then((r) => setPartners(r.data));
  }, []);

  useEffect(() => {
    api.get(`/reports/redditivita?year=${reddYear}`).then((r) => setRedd(r.data)).catch(() => setRedd(null));
  }, [reddYear]);

  const exportCsv = () => {
    const cols = ["name", "piva", "region", "province", "city", "agency_name", "certification_level",
      "importo_totale", "vesta_punti",
      "ultima_visita", "ultimo_acquisto"];
    const rows = partners.map((p) => cols.map((c) => `"${(p[c] ?? "").toString().replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([[cols.join(","), ...rows].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "report_partner_torri.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) return <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Report</h1>
          <p className="text-sm text-muted-foreground">Esporta e stampa i dati di partner e agenzie.</p>
        </div>
        <div className="flex gap-2">
          {user?.role !== "agency" && <Button variant="outline" onClick={exportCsv} data-testid="report-export-csv"><FileSpreadsheet className="mr-2 h-4 w-4" /> Esporta Excel/CSV</Button>}
          <Button onClick={() => window.print()} data-testid="report-print"><Printer className="mr-2 h-4 w-4" /> Stampa / PDF</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
        <Card className="p-5"><div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Vendite Totali</div><div className="mt-2 font-heading text-2xl font-black">{eur(data.kpis.total_revenue)}</div></Card>
        <Card className="p-5"><div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Partner</div><div className="mt-2 font-heading text-2xl font-black">{num(data.kpis.total_partners)}</div></Card>
        <Card className="p-5"><div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Punti Vesta medi</div><div className="mt-2 font-heading text-2xl font-black">{num(data.kpis.avg_vesta_punti)}</div></Card>
        <Card className="p-5"><div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Agenzie</div><div className="mt-2 font-heading text-2xl font-black">{num(data.kpis.active_agencies)}</div></Card>
      </div>

      <Card className="p-6">
        <div className="mb-4 flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-primary" />
          <h2 className="font-heading text-lg font-bold tracking-tight">Riepilogo per Regione</h2>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Regione</TableHead>
              <TableHead className="text-right">Partner</TableHead>
              <TableHead className="text-right">Vendite</TableHead>
              <TableHead className="text-right">% sul totale</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.region_distribution.map((r) => (
              <TableRow key={r.region} data-testid={`region-row-${r.region}`}>
                <TableCell className="font-semibold">{r.region}</TableCell>
                <TableCell className="text-right font-mono">{num(r.partners)}</TableCell>
                <TableCell className="text-right font-mono">{eur(r.revenue)}</TableCell>
                <TableCell className="text-right font-mono text-muted-foreground">
                  {data.kpis.total_revenue ? Math.round((r.revenue / data.kpis.total_revenue) * 100) : 0}%
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="p-6" data-testid="redditivita-card">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            <h2 className="font-heading text-lg font-bold tracking-tight">Redditività per Agente / Agenzia</h2>
          </div>
          <Select value={reddYear} onValueChange={setReddYear}>
            <SelectTrigger className="h-9 w-[160px]" data-testid="redd-year"><SelectValue placeholder="Anno" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli anni</SelectItem>
              {(redd?.years || []).map((y) => <SelectItem key={y} value={y}>Anno {y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {!redd ? (
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        ) : redd.rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nessuna vendita nel periodo selezionato.</p>
        ) : (
          <>
            <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
              <Card className="bg-muted/40 p-3"><div className="text-[10px] uppercase text-muted-foreground">Vendite</div><div className="font-heading text-xl font-black">{eur(redd.totals.fatturato)}</div></Card>
              <Card className="bg-muted/40 p-3"><div className="text-[10px] uppercase text-muted-foreground">Margine</div><div className="font-heading text-xl font-black">{eur(redd.totals.margine)}</div></Card>
              <Card className="bg-muted/40 p-3"><div className="text-[10px] uppercase text-muted-foreground">Margine medio</div><div className="font-heading text-xl font-black">{redd.totals.margine_pct}%</div></Card>
              <Card className="bg-muted/40 p-3"><div className="text-[10px] uppercase text-muted-foreground">kWp venduti</div><div className="font-heading text-xl font-black">{num(redd.totals.kwp)}</div></Card>
            </div>
            <div className="mb-4 flex flex-wrap gap-3">
              <Badge className="bg-emerald-600 text-white" data-testid="redd-top"><ArrowUp className="mr-1 h-3 w-3" /> Top: {redd.rows[0].agency} · {redd.rows[0].margine_pct}%</Badge>
              <Badge className="bg-red-600 text-white" data-testid="redd-bottom"><ArrowDown className="mr-1 h-3 w-3" /> Peggiore: {redd.rows[redd.rows.length - 1].agency} · {redd.rows[redd.rows.length - 1].margine_pct}%</Badge>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Agente / Agenzia</TableHead>
                  <TableHead className="text-right">Ordini</TableHead>
                  <TableHead className="text-right">kWp</TableHead>
                  <TableHead className="text-right">Vendite</TableHead>
                  <TableHead className="text-right">Margine €</TableHead>
                  <TableHead className="text-right">Margine %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {redd.rows.map((r) => (
                  <TableRow key={r.agency_id} data-testid={`redd-row-${r.agency_id}`}>
                    <TableCell className="font-semibold">{r.agency}{r.tipo === "agenzia" && <Badge variant="outline" className="ml-2 text-[10px]">Agenzia</Badge>}</TableCell>
                    <TableCell className="text-right font-mono">{num(r.ordini)}</TableCell>
                    <TableCell className="text-right font-mono">{num(r.kwp)}</TableCell>
                    <TableCell className="text-right font-mono">{eur(r.fatturato)}</TableCell>
                    <TableCell className="text-right font-mono">{eur(r.margine)}</TableCell>
                    <TableCell className="text-right font-mono">
                      <span className={r.margine_pct >= redd.totals.margine_pct ? "font-bold text-emerald-600" : "text-red-600"}>{r.margine_pct}%</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </Card>
    </div>
  );
}
